import $ from 'jquery';
import _, {isEmpty} from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {SequenceSummary} from 'constants/SequenceConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import notifications, {FOREVER_DURATION} from 'notifications';
import EntityStore from 'stores/EntityStore';
import getFileUploadMaxSizeBytes from 'util/getFileUploadMaxSizeBytes';
import {formatBytes} from 'util/NameUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {ProgressJQuery} from './Types';

const MAX_ATTEMPTS = 5;
const UPLOAD_DONE_PERCENT = 25;

// Legacy views may pass in a numeric folderId.
export type LegacyFolderId = string | number;

export interface ErrorResult {
  status: 'error';
  message: string;
}

export type SuccessResult = SequenceSummary & {
  status: 'success';
};

type SequenceImportResult = ErrorResult | SuccessResult;
export interface SequenceImportResponse {
  results: Array<SequenceImportResult>;
}

function importSequenceFromLocation(
  location: string,
  numAttempts: number,
  folderId: LegacyFolderId,
  multi?: boolean,
  nucleotideType?: NucleotideType,
  studyIds?: Array<string>
): JQueryPromise<SequenceImportResponse> {
  if (numAttempts >= MAX_ATTEMPTS) {
    return $.Deferred<SequenceImportResponse>().reject({maxAttemptsExceeded: true});
  }
  return TaskRunner.postJSON({
    url: '/1/api/import/import-request',
    data: {
      location,
      targetFolderId: folderId,
      multi,
      nucleotideType,
      studyIds,
    },
  }).then(
    ({
      results,
      status_code: statusCode,
    }: {
      results: Array<SequenceImportResult>;
      status_code: number;
    }): SequenceImportResponse | JQueryPromise<SequenceImportResponse> => {
      FolderDAOCache.invalidateFolderContents(folderId);
      if (statusCode === 409) {
        // Retry!
        return importSequenceFromLocation(
          location,
          numAttempts + 1,
          folderId,
          multi,
          nucleotideType,
          studyIds
        );
      }
      if (statusCode === 405) {
        return $.Deferred<SequenceImportResponse>().reject({
          message: (results[0] as ErrorResult).message,
          multipleSequencesFound: true,
        });
      }
      return {results};
    },
    (res) => $.Deferred<SequenceImportResponse>().reject(res),
    (progress) => UPLOAD_DONE_PERCENT + ((100 - UPLOAD_DONE_PERCENT) * progress) / 100
  );
}

function uploadFileToS3(
  file: File | Blob,
  {action, fields}: {action: string; fields: Array<{name: string; value: string}>},
  scalePercentProgress: (x: number) => number = (x) => x,
  numAttempts: number = 0
): JQueryPromise<string> {
  if (numAttempts >= MAX_ATTEMPTS) {
    return $.Deferred<string>().reject({
      maxAttemptsExceeded: true,
    });
  }
  const formData = new FormData();
  for (const field of fields) {
    formData.append(field.name, field.value);
  }
  // Order of fields is important, file has to go after res.fields.
  formData.append('file', file);
  const deferred: JQueryDeferred<string> = $.Deferred();
  ($ as ProgressJQuery)
    .ajax({
      url: action,
      type: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      progress(e: ProgressEvent): void {
        if (e.lengthComputable) {
          deferred.notify(scalePercentProgress(100 * (e.loaded / e.total)));
        }
      },
    })
    .done((_data, _status, xhr) => {
      deferred.notify(scalePercentProgress(100));
      const s3location = xhr.getResponseHeader('Location');
      deferred.resolve(s3location);
    })
    .fail((data, ...args: Array<unknown>) => {
      const statusCode = data.status;
      // Setting a hard ceiling. At max attempt = 5, max wait time is 5062.5ms ~ 5s
      const waitTime = Math.min(1.5 ** numAttempts * 1000, 5000);
      // when we get a 500/503 status from s3, aws recommends we retry
      if (statusCode === 500 || statusCode === 503) {
        setTimeout(() => {
          uploadFileToS3(file, {action, fields}, scalePercentProgress, numAttempts + 1)
            .done((s3location) => {
              deferred.resolve(s3location);
            })
            .fail(() => {
              deferred.reject(data, ...args);
            });
        }, waitTime);
      } else {
        deferred.reject(data, ...args);
      }
    });
  return deferred.promise();
}

// Makes a request to /1/api/import/upload-request to get data about a temporary S3 url to write to
function upload(file: File, scalePercentProgress: (x: number) => number = (x) => x): JQueryPromise<string> {
  const fileUploadMaxSizeBytes = getFileUploadMaxSizeBytes(EntityStore.currentUser());
  const fileName = file instanceof File ? file.name : 'noFileName.txt';
  assertAndContinue(isEmpty(fileName) === false, 'File has no filename.');
  assertAndContinue(file instanceof File, 'File is a Blob. Should turn blob into File.');
  if (file.size > fileUploadMaxSizeBytes) {
    const deferred: JQueryDeferred<string> = $.Deferred();
    return deferred.reject({
      userMessage: `File ${fileName} exceeds maximum upload size (${formatBytes(fileUploadMaxSizeBytes)}).`,
    });
  } else if (file.size === 0) {
    assertAndContinue(
      file.size !== 0,
      'User attempted upload of zero byte file - allowing upload to proceed.',
      {fileName},
      {level: 'info', fingerprint: ['benchling', 'ImportUtil', 'zero-byte-file']}
    );

    notifications.warn(`File ${fileName} is empty, but proceeding with upload...`, {
      duration: FOREVER_DURATION,
    });
  }
  return net
    .postJSON({
      url: '/1/api/import/upload-request',
      data: {fileType: file.type, fileName},
    })
    .then((res) => uploadFileToS3(file, res, scalePercentProgress));
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
const bulkUpload = (files: Array<File>) => {
  const fileUploadMaxSizeBytes = getFileUploadMaxSizeBytes(EntityStore.currentUser());
  if (files.some((file) => file.size > fileUploadMaxSizeBytes)) {
    const deferred: JQueryDeferred<string> = $.Deferred();
    return deferred.reject({
      userMessage: `A file exceeds maximum the upload size (${formatBytes(fileUploadMaxSizeBytes)}).`,
    });
  }
  return net
    .postJSON({
      url: '/1/api/import/bulk-upload-request',
      data: {
        numUploads: files.length,
        files: files.map(({name, type}) => ({name, type})),
      },
    })
    .then(({uploadInfos}): JQueryPromise<string> => {
      assert(
        files.length === uploadInfos.length,
        `Expected ${files.length} uploadInfos, got {uploadInfos.length}`
      );
      const uploadPromises = _.zip(files, uploadInfos).map(([file, requestInfo]) =>
        uploadFileToS3(file, requestInfo)
      );
      // If any of the uploads fail, treat this as if the entire bulkUpload failed. This may result
      // in some orphaned temporary files, which we're okay with.
      return $.when(...uploadPromises);
    });
};

const uploadSequence = (
  file: File,
  folderId: LegacyFolderId,
  multi: boolean = true,
  nucleotideType: NucleotideType = NucleotideType.DNA,
  studyIds: Array<string> = []
): JQueryPromise<SequenceImportResponse> => {
  assert(folderId != null && folderId !== -1, `Invalid folderId ${folderId} specified in importutil.upload`);
  // The file upload portion is expected to take up approximately UPLOAD_DONE_PERCENT of the time when importing
  // a sequence. Scale the progress reported in the returned promise to make the progress bar reflect this.
  return upload(file, (progress) => progress * (UPLOAD_DONE_PERCENT / 100)).then((s3location: string) =>
    importSequenceFromLocation(s3location, 0, folderId, multi, nucleotideType, studyIds)
  );
};

export {upload, bulkUpload, uploadSequence, uploadFileToS3};
